import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';
import ImageComponent from './ImageComponent';

const ALIGNMENTS = ['left', 'center', 'right'];

function normalizeAlignment(alignment) {
  return ALIGNMENTS.includes(alignment) ? alignment : 'left';
}

// Alignment travels as margins on the <img> itself so the store needs no CSS to honour it
function alignmentFromImageElement(domNode) {
  const { marginLeft, marginRight } = domNode.style;
  if (marginLeft === 'auto') {
    return marginRight === 'auto' ? 'center' : 'right';
  }
  for (let element = domNode.parentElement; element; element = element.parentElement) {
    const textAlign = element.style.textAlign || element.getAttribute('align');
    if (textAlign) {
      return normalizeAlignment(textAlign);
    }
  }
  return 'left';
}

function convertImageElement(domNode) {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src, width, height } = domNode;
    const node = $createImageNode({
      altText,
      height,
      src,
      width,
      alignment: alignmentFromImageElement(domNode),
    });
    return { node };
  }
  return null;
}

function applyAlignmentToWrapper(dom, alignment) {
  if (alignment === 'left') {
    dom.style.removeProperty('display');
    dom.style.removeProperty('text-align');
    return;
  }
  dom.style.display = 'block';
  dom.style.textAlign = alignment;
}

export class ImageNode extends DecoratorNode {
  __src = '';
  __altText = '';
  __width = 'inherit';
  __height = 'inherit';
  __maxWidth;
  __showCaption;
  __caption;
  // Captions cannot yet be used within editor cells
  __captionsEnabled;
  __isLoading = false;
  __id = undefined;
  __alignment = 'left';

  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__key,
      node.__isLoading,
      node.__id,
      node.__alignment,
    );
  }

  static importJSON(serializedNode) {
    const {
      altText,
      height,
      width,
      maxWidth,
      caption,
      src,
      showCaption,
      isLoading,
      id,
      alignment,
    } = serializedNode;

    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
      isLoading,
      id,
      alignment,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);

    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }

    return node;
  }

  exportDOM() {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    if (this.__alignment !== 'left') {
      element.style.display = 'block';
      element.style.marginLeft = 'auto';
      element.style.marginRight = this.__alignment === 'center' ? 'auto' : '0';
    }
    return { element };
  }

  static importDOM() {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src,
    altText,
    maxWidth,
    width,
    height,
    showCaption,
    caption,
    captionsEnabled,
    key,
    isLoading,
    id,
    alignment,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    this.__isLoading = isLoading || false;
    this.__id = id || undefined;
    this.__alignment = normalizeAlignment(alignment);
  }

  exportJSON() {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
      isLoading: this.__isLoading,
      id: this.__id,
      alignment: this.__alignment,
    };
  }

  getAlignment() {
    return this.__alignment;
  }

  setAlignment(alignment) {
    const writable = this.getWritable();
    writable.__alignment = normalizeAlignment(alignment);
  }

  setWidthAndHeight(width, height) {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption) {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  // View

  createDOM(config) {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    applyAlignmentToWrapper(span, this.__alignment);
    return span;
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__alignment !== this.__alignment) {
      applyAlignmentToWrapper(dom, this.__alignment);
    }
    return false;
  }

  getSrc() {
    return this.__src;
  }

  setSrc(newSrc) {
    const writable = this.getWritable();
    writable.__isLoading = false;
    writable.__src = newSrc;
  }

  getAltText() {
    return this.__altText;
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
          showCaption={this.__showCaption}
          caption={this.__caption}
          captionsEnabled={this.__captionsEnabled}
          resizable={true}
          isLoading={this.__isLoading}
          id={this.__id}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({
  altText,
  height,
  maxWidth,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  key,
  isLoading,
  id,
  alignment,
}) {
  return $applyNodeReplacement(
    new ImageNode(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      key,
      isLoading,
      id,
      alignment,
    ),
  );
}

export function $isImageNode(node) {
  return node instanceof ImageNode;
}
